import { useCommitsDoMes } from '../../api/queries/repositorios'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { Paginacao } from '../../components/Paginacao'
import { TEXTOS } from '../../lib/glossario'
import type { FiltrosDeCommits as Filtros } from '../../types/repositorios'
import { FiltrosDeCommits } from './FiltrosDeCommits'
import { LinhaDeCommit } from './LinhaDeCommit'

const TAMANHO = 50

interface Props {
  competencia: string
  mesFechado: boolean
  filtros: Filtros
  aoMudarFiltros: (filtros: Filtros) => void
}

/**
 * Os commits do mês, com filtros e paginação.
 *
 * O servidor recorta o mês pelo D-1 e devolve a página; a tela não conta nada sozinha. Os filtros
 * vivem na URL (quem chega pelo link "ver commits" de uma pessoa cai direto no recorte dela).
 *
 * @param props o mês, os filtros e o que fazer ao mudá-los
 * @returns a lista
 */
export function ListaDeCommits({ competencia, mesFechado, filtros, aoMudarFiltros }: Props) {
  const t = TEXTOS.repositorios.commits
  const { data, isPending, isError, error } = useCommitsDoMes(competencia, { ...filtros, tamanho: TAMANHO })
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / TAMANHO)) : 1

  return (
    <div className="space-y-4">
      <p className="flex max-w-prose items-start gap-1 text-sm text-slate-600">
        <span>{t.ajuda}</span>
        <Explicacao termo="branchPrincipal" />
      </p>
      <FiltrosDeCommits filtros={filtros} mesFechado={mesFechado} aoMudar={aoMudarFiltros} />
      {isPending && <Esqueleto linhas={6} />}
      {isError && <AvisoDeErro erro={error} />}
      {data && data.itens.length === 0 && <EstadoVazio titulo={t.semResultado.titulo} ajuda={t.semResultado.ajuda} />}
      {data && data.itens.length > 0 && (
        <div className="cartao overflow-x-auto p-4">
          <table className="min-w-full text-sm">
            <caption className="sr-only">{t.ajuda}</caption>
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
                <th className="px-3 py-2">{t.colunas.quando}</th>
                <th className="px-3 py-2">{t.colunas.quem}</th>
                <th className="px-3 py-2">{t.colunas.onde}</th>
                <th className="px-3 py-2">{t.colunas.oQue}</th>
                <th className="px-3 py-2">{t.colunas.solicitacao}</th>
                <th className="px-3 py-2 text-right">{t.colunas.linhas}</th>
              </tr>
            </thead>
            <tbody>
              {data.itens.map((commit) => (
                <LinhaDeCommit key={commit.id} commit={commit} />
              ))}
            </tbody>
          </table>
          <div className="mt-4">
            <Paginacao
              pagina={(filtros.pagina ?? 0) + 1}
              totalPaginas={totalPaginas}
              totalElementos={data.total}
              aoMudar={(numero) => aoMudarFiltros({ ...filtros, pagina: numero - 1 })}
              nomeDoItem={{ singular: 'commit', plural: 'commits' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
